import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Convierte una clave VAPID de base64 a Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Hook para gestionar Push Notifications
 *
 * @param {string} restaurantId - ID del restaurante
 * @param {string} clientType - Tipo de cliente ('admin', 'comanda', 'customer')
 * @param {string} userId - ID del usuario (para admin/comanda)
 * @param {string} sessionId - ID de sesión (para customer)
 */
export function usePushNotifications(restaurantId, clientType, userId, sessionId) {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Verificar soporte del navegador
  useEffect(() => {
    const supported =
      'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Verificar si ya está subscrito
  useEffect(() => {
    if (!isSupported) return;

    const checkSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (err) {
        console.error('[Push] Error checking subscription:', err);
      }
    };

    checkSubscription();
  }, [isSupported]);

  /**
   * Subscribirse a push notifications
   */
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications no soportadas en este navegador');
      return false;
    }

    if (!restaurantId) {
      setError('Se requiere restaurantId para subscribirse');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 1. Pedir permiso
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        setError('Permiso de notificaciones denegado');
        setIsLoading(false);
        return false;
      }

      // 2. Obtener VAPID public key del backend
      const vapidResponse = await fetch(`${API_URL}/notifications/vapid-public-key`);
      if (!vapidResponse.ok) {
        throw new Error('Error obteniendo VAPID key');
      }
      const { publicKey } = await vapidResponse.json();

      // 3. Subscribirse al Push Manager
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      // 4. Enviar subscription al backend
      const subscribeResponse = await fetch(`${API_URL}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          restaurantId,
          clientType,
          userId,
          sessionId,
        }),
      });

      if (!subscribeResponse.ok) {
        throw new Error('Error guardando subscription en servidor');
      }

      setIsSubscribed(true);
      console.log('[Push] Subscribed successfully');
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('[Push] Subscribe error:', err);
      setError(err.message);
      setIsLoading(false);
      return false;
    }
  }, [isSupported, restaurantId, clientType, userId, sessionId]);

  /**
   * Desuscribirse de push notifications
   */
  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Eliminar del navegador
        await subscription.unsubscribe();

        // Eliminar del servidor
        await fetch(`${API_URL}/notifications/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint,
          }),
        });
      }

      setIsSubscribed(false);
      console.log('[Push] Unsubscribed successfully');
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('[Push] Unsubscribe error:', err);
      setError(err.message);
      setIsLoading(false);
      return false;
    }
  }, []);

  return {
    permission,
    isSubscribed,
    isSupported,
    isLoading,
    error,
    subscribe,
    unsubscribe,
  };
}

export default usePushNotifications;
